import { call, takeLatest, put, takeEvery } from 'redux-saga/effects';
import ProductServices from 'shared/services/ProductService';
import {
  createProductData,
  getProductData,
  setProductData,
  actionFail,
  updateProductSuccess,
  updateProductFail,
  updateProductData,
  buildProductPage,
  setIndividualProductInfo,
  setProductVariationsData,
  getECommerceProductVariationsData,
  mapEcommerceProductVariation,
  mapEcommerceProductVariationSuccess,
  mapEcommerceProductVariationFail
} from './product.redux';
import {
  TAction,
  TPaginationRequest,
  TCreateProduct,
  TUpdateJourneyTemplate as TUpdateProduct
} from 'shared/types';

import { openNotificationMessage } from '../../shared/utils/NotificationMessages';
import { NotificationMessages } from 'shared/enums';
import { AxiosResponse } from 'axios';
import { IJourneyTemplate as IProduct } from 'shared/interfaces/journeyTemplate.interface';

function* getProduct({ payload }: TAction<TPaginationRequest>) {
  try {
    const result: AxiosResponse<IProduct[]> = yield call(
      ProductServices.getProductInfo,
      payload
    );
    yield put(setProductData(result));
  } catch (error) {
    console.log('error', error);
  }
}

function* createProduct({ payload }: TAction<TCreateProduct>) {
  try {
    yield call(ProductServices.createProduct, payload.productData);
    yield put(getProductData(payload?.pagination));
    yield openNotificationMessage({
      type: NotificationMessages.Success,
      title: 'Success',
      message: 'Product was successfully created.'
    });
  } catch (error: any) {
    console.log('error', error);
    yield put(actionFail());
    yield openNotificationMessage({
      type: NotificationMessages.Error,
      title: 'Error',
      message:
        error?.response?.data.message || 'Product creation was unsuccessful.'
    });
  }
}

function* handleUpdateProduct({ payload }: TAction<TUpdateProduct>) {
  try {
    const result: AxiosResponse<IProduct> = yield call(
      ProductServices.updateProduct,
      payload
    );
    yield put(updateProductSuccess(result));
    yield openNotificationMessage({
      type: NotificationMessages.Success,
      title: 'Success',
      message: 'Product was successfully updated.'
    });
  } catch (error: any) {
    yield put(updateProductFail());
    console.log('error', error);
    yield openNotificationMessage({
      type: NotificationMessages.Error,
      title: 'Error',
      message:
        error?.response?.data?.message || 'Product updating was unsuccessful.'
    });
  }
}

function* buildIndividualPage({ payload }: TAction<any>) {
  try {
    const result: AxiosResponse<IProduct[]> = yield call(
      ProductServices.getOneProduct,
      payload.id
    );
    yield put(setIndividualProductInfo(result));
  } catch (error) {
    console.log('error', error);
  }
}

function* getEcommerceProductVariations({
  payload
}: TAction<TPaginationRequest>) {
  try {
    const result: AxiosResponse<IProduct[]> = yield call(
      ProductServices.getProductVariationsInfo,
      payload
    );
    yield put(setProductVariationsData(result));
  } catch (error) {
    console.log('error', error);
  }
}

function* mapEcommerceProductVariationSaga({ payload }: TAction<any>) {
  try {
    // payload should contain epvtId (the epvt::<id> identifier) and productVariationId
    const { epvtId, productVariationId, pagination } = payload || {};
    const body = {
      productVariationId
    };
    const result: AxiosResponse<any> = yield call(
      ProductServices.mapEcommerceProductVariation,
      epvtId,
      body
    );
    yield put(mapEcommerceProductVariationSuccess(result));
    yield openNotificationMessage({
      type: NotificationMessages.Success,
      title: 'Success',
      message: 'Product variation was successfully mapped.'
    });

    // Refresh product variations list after mapping
    yield put(
      getECommerceProductVariationsData(
        pagination || {
          page: 1,
          limit: 20
        }
      )
    );
  } catch (error: any) {
    console.log('error', error);
    yield put(mapEcommerceProductVariationFail());
    yield openNotificationMessage({
      type: NotificationMessages.Error,
      title: 'Error',
      message:
        error?.response?.data?.message ||
        'Product variation mapping was unsuccessful.'
    });
  }
}

function* Products() {
  yield takeLatest(getProductData.type, getProduct);
  yield takeEvery(createProductData.type, createProduct);
  yield takeEvery(updateProductData.type, handleUpdateProduct);
  yield takeLatest(buildProductPage.type, buildIndividualPage);
  yield takeLatest(
    getECommerceProductVariationsData.type,
    getEcommerceProductVariations
  );
  yield takeEvery(
    mapEcommerceProductVariation.type,
    mapEcommerceProductVariationSaga
  );
}

export default Products;
